/**
 * Pure helpers for the Concerns feature: episode-duration formatting, the
 * "latest severity" reducer for a note timeline, the translation of concern
 * bands into an ECharts markArea data shape, and the since-onset baseline delta.
 * All pure; unit-tested.
 */

import { fmtNumber } from './format.js';

/**
 * Human span between `openedAt` and `closedAt` (or `now`, for an open concern).
 * Floors to the largest whole unit — days ≥ 1d, else hours ≥ 1h, else minutes —
 * so "3d" / "6h" read as elapsed time, never rounded up. Invalid input → "—".
 */
export function concernDuration(
  openedAt: string,
  closedAt: string | null,
  now: Date = new Date(),
): string {
  const start = Date.parse(openedAt);
  const end = closedAt !== null ? Date.parse(closedAt) : now.getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return '—';
  const ms = Math.max(0, end - start);
  const minutes = ms / 60_000;
  if (minutes < 60) return `${Math.floor(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.floor(hours)}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Minimal shape the severity reducer needs from a concern note. */
export interface SeverityNote {
  notedAt: string;
  severity: number | null;
}

/**
 * The severity of the most recent note that carries one (newer notes without a
 * severity are skipped so a bare text note doesn't erase the last rating).
 * Returns null when no note has a severity.
 */
export function latestSeverity(notes: readonly SeverityNote[]): number | null {
  let best: { at: number; severity: number } | null = null;
  for (const n of notes) {
    if (n.severity === null) continue;
    const at = Date.parse(n.notedAt);
    if (!Number.isFinite(at)) continue;
    if (best === null || at > best.at) best = { at, severity: n.severity };
  }
  return best ? best.severity : null;
}

/**
 * Palette slot (1-based, see metrics.PALETTE) a severity dot wears. A warm
 * escalation drawn entirely from existing categorical slots — green → amber →
 * orange → pink → red — so higher severities read hotter without inventing a new
 * colour. Out-of-range values clamp to the ends.
 */
const SEVERITY_SLOTS = [2, 3, 8, 7, 6] as const;

export function severitySlot(severity: number): number {
  const idx = Math.min(SEVERITY_SLOTS.length, Math.max(1, Math.round(severity))) - 1;
  return SEVERITY_SLOTS[idx]!;
}

/** A concern band over the chart's time axis; `to === null` runs to chart end. */
export interface ChartBand {
  from: string;
  to: string | null;
  label: string;
}

/** One ECharts markArea entry: a [start, end] pair, label carried on the start. */
export type MarkAreaPair = [{ xAxis: string; name: string }, { xAxis: string }];

/**
 * Translate concern bands into the ECharts `markArea.data` shape — an array of
 * start/end coordinate pairs. Open-ended bands (`to === null`) extend to
 * `chartEnd` so the shaded region reaches the right edge of the plot.
 */
export function bandsToMarkArea(bands: readonly ChartBand[], chartEnd: string): MarkAreaPair[] {
  return bands.map((b) => [
    { xAxis: b.from, name: b.label },
    { xAxis: b.to ?? chartEnd },
  ]);
}

/**
 * A concern's timeline is two interleaved streams: hand-written notes (dated by
 * `notedAt`) and the assistant's cited assessments (dated by `createdAt`).
 * `mergeTimeline` folds them into one chronological list as a tagged union so the
 * page can render each kind distinctly. Generic over the concrete shapes so it
 * stays pure and unit-testable with minimal fixtures.
 */
export type TimelineEntry<N, A> =
  | { kind: 'note'; at: number; item: N }
  | { kind: 'assessment'; at: number; item: A };

/**
 * Stable ascending (oldest-first) merge of notes and assessments. Ties break to
 * input order (notes are appended before assessments, so a note and an
 * assessment sharing a timestamp render note-then-assessment). Entries whose
 * timestamp won't parse sink to the end without reordering among themselves.
 */
export function mergeTimeline<N extends { notedAt: string }, A extends { createdAt: string }>(
  notes: readonly N[],
  assessments: readonly A[],
): Array<TimelineEntry<N, A>> {
  const entries: Array<TimelineEntry<N, A>> = [
    ...notes.map((n) => ({ kind: 'note' as const, at: Date.parse(n.notedAt), item: n })),
    ...assessments.map((a) => ({ kind: 'assessment' as const, at: Date.parse(a.createdAt), item: a })),
  ];
  return entries
    .map((e, i) => ({ e, i }))
    .sort((x, y) => {
      const ax = Number.isFinite(x.e.at) ? x.e.at : Infinity;
      const ay = Number.isFinite(y.e.at) ? y.e.at : Infinity;
      return ax - ay || x.i - y.i;
    })
    .map((w) => w.e);
}

/** Filled-dot count (of three) for an episode contributor's confidence level. */
export function confidenceDots(level: 'low' | 'medium' | 'high'): number {
  return { low: 1, medium: 2, high: 3 }[level] ?? 0;
}

/** The minimum since-onset days a delta needs before it's worth showing. */
export const MIN_SINCE_ONSET_DAYS = 3;

export interface SinceOnsetDelta {
  /** Count of day-values contributing to the average. */
  n: number;
  /** Mean of the since-onset day-values. */
  avg: number;
  /** avg − baseline.mean (positive = above the personal norm). */
  delta: number;
  /** Human label of the delta, e.g. "+3 vs your norm". */
  label: string;
}

/**
 * Average a metric's since-onset day-values and express the gap from the user's
 * personal baseline. Returns null when there's no usable baseline or fewer than
 * MIN_SINCE_ONSET_DAYS days of data — a couple of readings during an episode
 * aren't enough to claim a shift from normal.
 */
export function sinceOnsetDelta(
  points: readonly { v: number }[],
  baseline: { mean: number } | null | undefined,
): SinceOnsetDelta | null {
  if (!baseline || !Number.isFinite(baseline.mean)) return null;
  const vals = points.map((p) => p.v).filter((v) => Number.isFinite(v));
  if (vals.length < MIN_SINCE_ONSET_DAYS) return null;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  const delta = avg - baseline.mean;
  const rounded = Number(delta.toFixed(1));
  const sign = rounded > 0 ? '+' : '';
  const label = rounded === 0 ? 'at your norm' : `${sign}${fmtNumber(rounded)} vs your norm`;
  return { n: vals.length, avg, delta, label };
}
